import React, { useEffect, useState, useCallback } from "react";
import { db, auth } from "../../firebase";
import {
    collection, getDocs, query, where, addDoc, updateDoc,
    deleteDoc, doc, getDoc, serverTimestamp,
    orderBy
} from "firebase/firestore";
import {
    Container, Paper, Typography, Grid, TextField, MenuItem,
    Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Box, CircularProgress,
    TablePagination, InputAdornment, Autocomplete
} from "@mui/material";
import { Delete, Edit, Save, Search, DirectionsBus, Close } from "@mui/icons-material";

const PRIMARY = "#0d9488";

function Studentassign() {
    const [loading, setLoading] = useState(true);
    const [schoolId, setSchoolId] = useState("");
    const [mergedStudents, setMergedStudents] = useState([]);
    const [locationTypes, setLocationTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [assignments, setAssignments] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        id: null,
        admissionId: "",
        locationTypeName: "",
        locationId: "",
        amount: 0,
        date: new Date().toISOString().split("T")[0],
        status: "Pending"
    });

    // 1. Fetch Data Function (wrapped in useCallback for reliability)
    const fetchInitialData = useCallback(async () => {
        try {
            setLoading(true);
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            const sId = userSnap.exists() ? userSnap.data().schoolId : "zIlbJM5BjyrlEhg7fWKs";
            setSchoolId(sId);

            const [admSnap, studentSnap, typeSnap, assignSnap, locSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "location_types"), where("schoolId", "==", sId), orderBy('fee', "asc"))),
                getDocs(query(collection(db, "student_assignments"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "locations"), where("schoolId", "==", sId)))
            ]);

            const studentMap = {};
            studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

            setLocationTypes(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const dayStudents = admSnap.docs.map(doc => {
                const adm = { id: doc.id, ...doc.data() };
                const sData = studentMap[adm.studentId] || {};
                return {
                    ...adm,
                    fullName: `${sData.firstname || ""} ${sData.fathername || ""} ${sData.surname || ""}`.trim().toUpperCase(),
                    studenttype: sData.studenttype || "Day School"
                };
            }).filter(s => s.studenttype === "Day School");

            setMergedStudents(dayStudents);
            setAssignments(assignSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // 2. Handle Save (Fixed Update logic)
    const handleSave = async () => {
        if (!formData.admissionId || !formData.locationId) return alert("Select Student and Location");

        try {
            setLoading(true);
            const { id, ...dataToSave } = formData;
            const finalData = { ...dataToSave, schoolId, updatedAt: serverTimestamp() };

            if (id) {
                // Correct path for UpdateDoc
                const docRef = doc(db, "student_assignments", id);
                await updateDoc(docRef, finalData);
            } else {
                await addDoc(collection(db, "student_assignments"), {
                    ...finalData,
                    createdAt: serverTimestamp()
                });
            }

            resetForm();
            await fetchInitialData(); // Wait for re-fetch
        } catch (e) {
            console.error("Save Error:", e);
            alert("Error saving data");
        } finally {
            setLoading(false);
        }
    };

    // 3. Handle Delete (Wait for completion)
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this assignment?")) {
            try {
                setLoading(true);
                await deleteDoc(doc(db, "student_assignments", id));
                await fetchInitialData(); // Wait for re-fetch to update table
            } catch (err) {
                console.error("Delete Error:", err);
            } finally {
                setLoading(false);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            admissionId: "",
            locationTypeName: "",
            locationId: "",
            amount: 0,
            date: new Date().toISOString().split("T")[0],
            status: "Pending"
        });
    };

    const filteredAssignments = assignments.filter(row => {
        const student = mergedStudents.find(s => s.id === row.admissionId);
        const loc = locations.find(l => l.id === row.locationId);
        const s = search.toLowerCase();
        return (
            student?.fullName?.toLowerCase().includes(s) ||
            row.locationTypeName?.toLowerCase().includes(s) ||
            loc?.locationName?.toLowerCase().includes(s)
        );
    });

    const paginatedData = filteredAssignments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading && assignments.length === 0) return <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}><CircularProgress /></Box>;

    return (
        <Box  sx={{ py: 1 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color={PRIMARY} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DirectionsBus /> {formData.id ? "Edit" : "Assign"} Transport Route
            </Typography>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} variant="outlined">
                <Grid className="row" spacing={2}>
                    <Grid className="col-sm-3" item xs={12} md={4}>
                        <Autocomplete
                            size="small"
                            options={mergedStudents}
                            getOptionLabel={(o) => o.fullName || ""}
                            value={mergedStudents.find(s => s.id === formData.admissionId) || null}
                            onChange={(e, val) => setFormData(prev => ({ ...prev, admissionId: val?.id || "" }))}
                            renderInput={(params) => <TextField {...params} label="Select Student" />}
                        />
                    </Grid>

                    <Grid className="col-sm-3" item xs={12} md={3}>
                        <TextField select fullWidth size="small" label="Route Type" value={formData.locationTypeName}
                            onChange={e => {
                                const t = locationTypes.find(type => type.name === e.target.value);
                                setFormData(prev => ({ ...prev, locationTypeName: e.target.value, locationId: "", amount: t?.fee || 0 }));
                            }}>
                            {locationTypes.map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                        </TextField>
                    </Grid>

                    <Grid className="col-sm-2" item xs={12} md={3}>
                        <TextField select fullWidth size="small" label="Location" value={formData.locationId} disabled={!formData.locationTypeName}
                            onChange={e => setFormData(prev => ({ ...prev, locationId: e.target.value }))}>
                            {locations.filter(l => l.locationType === formData.locationTypeName).map(l => (
                                <MenuItem key={l.id} value={l.id}>{l.locationName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid className="col-sm-2" item xs={12} md={2}>
                        <TextField size="small" fullWidth type="number" label="Fee" value={formData.amount} onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))} />
                    </Grid>

                    <Grid className="col-sm-2" item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {formData.id && <Button size="small" onClick={resetForm} startIcon={<Close />}>Cancel</Button>}
                        <Button variant="contained" disabled={loading} onClick={handleSave} sx={{ bgcolor: PRIMARY, height: 35, px: 4 }} startIcon={<Save />}>
                            {formData.id ? "Update" : "Save"}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* SEARCH & PAGINATION UI */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    sx={{ width: 300 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                />
                <TablePagination
                    component="div"
                    count={filteredAssignments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                        <TableRow>
                            <TableCell width={60}><strong>Sr.</strong></TableCell>
                            <TableCell><strong>Student</strong></TableCell>
                            <TableCell><strong>Route / Point</strong></TableCell>
                            <TableCell><strong>Fee</strong></TableCell>
                            <TableCell align="right"><strong>Action</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedData.map((row, index) => {
                            const student = mergedStudents.find(s => s.id === row.admissionId);
                            const loc = locations.find(l => l.id === row.locationId);
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{student?.fullName || "N/A"}</TableCell>
                                    <TableCell>
                                        <Box sx={{ color: PRIMARY, fontSize: '0.75rem', fontWeight: 700 }}>{row.locationTypeName}</Box>
                                        {loc?.locationName || "N/A"}
                                    </TableCell>
                                    <TableCell>₹{row.amount}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => { setFormData(row); window.scrollTo({ top: 0, behavior: 'smooth' }); }} size="small" color="primary"><Edit fontSize="small" /></IconButton>
                                        <IconButton onClick={() => handleDelete(row.id)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {paginatedData.length === 0 && (
                            <TableRow><TableCell colSpan={5} align="center">No assignments found</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default Studentassign;